import axios from 'axios';
import { ConfirmOrCancel } from 'components/forms/ConfirmOrCancel';
import { BaseModal } from 'components/modals/BaseModal';
import { Card, CardHeader, CardHeaderContainer } from 'core/layout';
import { ErrorMessage } from 'core/typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { sleep } from 'utils/timeUtils';
import useStore from 'zstore';

const ErrorContainer = styled.div`
  margin-top: 12px;
`;

interface IProps {
  courseId: string;
  courseName: string;
  userId: string;
  userEmail: string;
  userName: string;
  close: () => void;
  refetch: () => void;
}

export const ResetLearnerProgressModal = React.memo<IProps>((props) => {
  const { t } = useTranslation();
  const { courseId, courseName, userId, userEmail, userName } = props;
  const [confirmIsPending, setConfirmIsPending] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { makeToast } = useStore();

  const handleConfirmClick = async () => {
    setConfirmIsPending(true);
    setHasError(false);
    setErrorMessage('');
    try {
      await axios.post(`/api/v4/courses/${courseId}/learners/${userId}/reset_progress`);

      setConfirmIsPending(false);
      makeToast({
        text: t('Progress reset started for {{learnerName}}', { learnerName: userName || userEmail }),
        isError: false,
      });
      props.close();

      // Reload learner data after toast finishes (6 seconds)
      await sleep(6000);
      props.refetch();
    } catch (err: any) {
      setConfirmIsPending(false);
      setHasError(true);

      // Extract first error title from errors array
      const firstErrorTitle = err?.response?.data?.errors?.[0]?.title || t('Oops! Something went wrong. Please try again.');
      setErrorMessage(firstErrorTitle);
    }
  };

  return (
    <BaseModal title={t('Reset Learner Progress')} onClose={props.close}>
      <Card>
        <CardHeaderContainer>
          <CardHeader>
            {t('Reset {{learnerName}}\'s progress in {{courseName}}? This cannot be undone.', {
              learnerName: userName || userEmail,
              courseName
            })}
          </CardHeader>
        </CardHeaderContainer>
      </Card>

      <ConfirmOrCancel
        cancelText={t('Cancel')}
        confirmText={t('Reset Progress')}
        handleCancelClick={props.close}
        handleConfirmClick={handleConfirmClick}
        confirmIsPending={confirmIsPending}
        confirmIsDisabled={false}
        destructive={true}
      />

      {hasError && (
        <ErrorContainer>
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </ErrorContainer>
      )}
    </BaseModal>
  );
});
